/**
 * WorkspaceHistorySettings — a workspace's version-history default (file
 * areas §4.12): "Keep version history" on or off, and "Versions to keep"
 * (1–10). A file's own choice, set on its detail page, outranks it; the Save
 * dialog's answer outranks both.
 *
 * Personal: its owner changes it. A shared workspace: admins and owners;
 * everyone else sees it read-only. Turning history off deletes nothing — it
 * only stops new versions.
 */
import { useState } from 'react';
import { History } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import {
  useUpdateWorkspaceFileSettings,
  useWorkspaceFileSettings,
} from '../hooks/useVaultDocuments';

const VERSION_CHOICES = Array.from({ length: 10 }, (_, i) => i + 1);

export function WorkspaceHistorySettings({ holderId }: { holderId: string | null }) {
  const query = useWorkspaceFileSettings(holderId);
  const update = useUpdateWorkspaceFileSettings(holderId);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const settings = query.data?.settings;
  if (!holderId || !settings) return null;
  const canChange = query.data?.canChange === true;

  const save = (patch: { historyEnabled?: boolean; historyMaxVersions?: number }) => {
    setError(null);
    update.mutate(patch, {
      onError: (err) => setError(err instanceof Error ? err.message : 'That didn’t save.'),
    });
  };

  return (
    <>
      <button
        type="button"
        className="inline-flex items-center gap-1.5 self-start text-xs text-muted-foreground underline"
        onClick={() => setOpen(true)}
      >
        <History className="h-3.5 w-3.5" aria-hidden />
        Version history:{' '}
        {settings.historyEnabled
          ? `keep ${settings.historyMaxVersions} ${settings.historyMaxVersions === 1 ? 'version' : 'versions'}`
          : 'off'}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Version history</DialogTitle>
            <DialogDescription>
              When a file is changed or replaced, earlier versions can be kept so you can go back.
              They count toward your storage. A file’s own setting outranks this one.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <label className="flex items-center justify-between gap-3 text-sm">
              <span>Keep version history</span>
              <Switch
                checked={settings.historyEnabled}
                disabled={!canChange || update.isPending}
                onCheckedChange={(checked) => save({ historyEnabled: checked })}
                aria-label="Keep version history"
              />
            </label>
            <label className="flex items-center justify-between gap-3 text-sm">
              <span>Versions to keep</span>
              <select
                value={settings.historyMaxVersions}
                disabled={!canChange || update.isPending || !settings.historyEnabled}
                onChange={(e) => save({ historyMaxVersions: Number(e.target.value) })}
                className="rounded border border-border bg-card px-2 py-1 text-sm"
              >
                {VERSION_CHOICES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            {!settings.historyEnabled && (
              <p className="text-xs text-muted-foreground">
                New versions aren’t kept. Nothing already kept is deleted — you can remove old
                versions from a file’s page.
              </p>
            )}
            {settings.historyEnabled && (
              <p className="text-xs text-muted-foreground">
                When a file already has this many, its oldest version gives way to the next one.
              </p>
            )}
            {!canChange && (
              <p className="text-xs text-muted-foreground">
                Only workspace admins can change this.
              </p>
            )}
            {error && (
              <p role="alert" className="text-xs text-destructive">
                {error}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
